import { useState, useEffect } from 'react'
import supabase from '../../lib/supabase'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'

function AdminHome() {
  const [stats, setStats] = useState({
    leadsCount: 0,
    servicesCount: 0,
    usersCount: 0
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [user, setUser] = useState(null)
  const [adminStatus, setAdminStatus] = useState({
    isAdmin: false,
    currentUser: 'Unknown',
    currentRole: 'Unknown'
  })

  useEffect(() => {
    const init = async () => {
      try {
        const {
          data: { user: authUser }
        } = await supabase.auth.getUser()

        setUser(authUser || null)

        if (authUser?.email) {
          const { data: profile, error: profileError } = await supabase
            .from('codevaro_users')
            .select('email, role, created_at')
            .eq('email', authUser.email)
            .maybeSingle()

          if (profileError) {
            console.error('Error fetching profile:', profileError)
          }

          setAdminStatus({
            isAdmin: profile?.role === 'admin' || profile?.role === 'superadmin',
            currentUser: profile?.email || authUser.email || 'Unknown',
            currentRole: profile?.role || 'Unknown'
          })
        }

        await fetchStats()
      } catch (err) {
        console.error('Error initializing AdminHome:', err)
        setError('Σφάλμα κατά τη φόρτωση του dashboard')
      } finally {
        setLoading(false)
      }
    }

    init()
  }, [])

  const fetchStats = async () => {
    try {
      const nextStats = {
        leadsCount: 0,
        servicesCount: 0,
        usersCount: 0
      }

      try {
        const { count, error } = await supabase
          .from('codevaro_leads')
          .select('*', { count: 'exact', head: true })

        if (!error) nextStats.leadsCount = count || 0
        else console.warn('Leads count error:', error)
      } catch (err) {
        console.warn('Leads count exception:', err)
      }

      try {
        const { count, error } = await supabase
          .from('codevaro_services')
          .select('*', { count: 'exact', head: true })

        if (!error) nextStats.servicesCount = count || 0
        else console.warn('Services count error:', error)
      } catch (err) {
        console.warn('Services count exception:', err)
      }

      try {
        const { count, error } = await supabase
          .from('codevaro_users')
          .select('*', { count: 'exact', head: true })

        if (!error) nextStats.usersCount = count || 0
        else console.warn('Users count error:', error)
      } catch (err) {
        console.warn('Users count exception:', err)
      }

      setStats(nextStats)
    } catch (err) {
      console.error('Error fetching stats:', err)
      setError('Σφάλμα κατά τη φόρτωση των στατιστικών')
    }
  }

  const roleLabel =
    adminStatus.currentRole === 'superadmin'
      ? 'Κύριος Διαχειριστής'
      : adminStatus.currentRole === 'admin'
      ? 'Διαχειριστής'
      : adminStatus.currentRole || 'Unknown'

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Πίνακας Ελέγχου</h1>

      {error && (
        <div
          className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="mt-6">
        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
          <div className="bg-white overflow-hidden shadow rounded-lg flex-1">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                  <SafeIcon icon={FiIcons.FiUsers} className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Συνολικά Leads
                    </dt>
                    <dd>
                      <div className="text-lg font-medium text-gray-900">
                        {loading ? '...' : stats.leadsCount}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg flex-1">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                  <SafeIcon icon={FiIcons.FiLayers} className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Υπηρεσίες
                    </dt>
                    <dd>
                      <div className="text-lg font-medium text-gray-900">
                        {loading ? '...' : stats.servicesCount}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg flex-1">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                  <SafeIcon icon={FiIcons.FiUserPlus} className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Χρήστες
                    </dt>
                    <dd>
                      <div className="text-lg font-medium text-gray-900">
                        {loading ? '...' : stats.usersCount}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 bg-blue-50">
          <h3 className="text-lg leading-6 font-medium text-blue-800">
            Πληροφορίες Διαχειριστή
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-blue-600">
            Στοιχεία λογαριασμού και δικαιώματα πρόσβασης.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {user?.email || 'Μη διαθέσιμο'}
              </dd>
            </div>

            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Κατάσταση Διαχειριστή</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                {adminStatus.isAdmin ? (
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    {roleLabel}
                  </span>
                ) : (
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                    Δεν είναι διαχειριστής
                  </span>
                )}
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Τρέχων Χρήστης</dt>
              <dd className="mt-1 text-sm font-mono text-gray-900 sm:mt-0 sm:col-span-2">
                {adminStatus.currentUser}
              </dd>
            </div>

            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Τρέχων Ρόλος</dt>
              <dd className="mt-1 text-sm font-mono text-gray-900 sm:mt-0 sm:col-span-2">
                {adminStatus.currentRole}
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">ID Χρήστη</dt>
              <dd className="mt-1 text-sm font-mono text-gray-900 sm:mt-0 sm:col-span-2">
                {user?.id || 'Μη διαθέσιμο'}
              </dd>
            </div>

            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Τελευταία Σύνδεση</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {user?.last_sign_in_at
                  ? new Date(user.last_sign_in_at).toLocaleString('el-GR')
                  : 'Μη διαθέσιμο'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 bg-blue-50">
          <h3 className="text-lg leading-6 font-medium text-blue-800">
            Οδηγός Εγκατάστασης
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-blue-600">
            Ακολουθήστε τα παρακάτω βήματα για να ρυθμίσετε την εφαρμογή σας.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <ul className="divide-y divide-gray-200">
            <li className="px-4 py-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-green-100 rounded-full p-1">
                  <SafeIcon icon={FiIcons.FiCheck} className="h-5 w-5 text-green-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Σύνδεση διαχειριστή</p>
                  <p className="text-sm text-gray-500">
                    Έχετε συνδεθεί επιτυχώς ως διαχειριστής.
                  </p>
                </div>
              </div>
            </li>
            <li className="px-4 py-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-blue-100 rounded-full p-1">
                  <SafeIcon icon={FiIcons.FiDatabase} className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Ρύθμιση βάσης δεδομένων</p>
                  <p className="text-sm text-gray-500">
                    Δημιουργήστε τους απαραίτητους πίνακες από το μενού "Ρυθμίσεις" &gt;
                    "Επιδιόρθωση Βάσης".
                  </p>
                </div>
              </div>
            </li>
            <li className="px-4 py-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-yellow-100 rounded-full p-1">
                  <SafeIcon icon={FiIcons.FiLayers} className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Προσθήκη υπηρεσιών</p>
                  <p className="text-sm text-gray-500">
                    Προσθέστε τις υπηρεσίες σας από το μενού "Υπηρεσίες".
                  </p>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default AdminHome